import React, { useState, useEffect } from 'react';
import { knowledgeBaseAPI } from '../services/api';
import { BookOpen, Plus, Trash2, Search, Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function KnowledgeBase() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    documentTitle: '', documentType: '', state: '', regulationCitation: '', content: '', tags: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'text'
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadDocuments(); }, []);

  const loadDocuments = async () => {
    try {
      const res = await knowledgeBaseAPI.getAll();
      setDocuments(res.data.documents || []);
    } catch (err) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (uploadMode === 'file' && uploadFile) {
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('documentTitle', formData.documentTitle);
        fd.append('documentType', formData.documentType || '');
        fd.append('state', formData.state || '');
        fd.append('regulationCitation', formData.regulationCitation || '');
        fd.append('tags', formData.tags || '');
        await knowledgeBaseAPI.upload(fd);
      } else {
        await knowledgeBaseAPI.create({
          ...formData,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
        });
      }
      toast.success('Document added to knowledge base!');
      setShowModal(false);
      setFormData({ documentTitle: '', documentType: '', state: '', regulationCitation: '', content: '', tags: '' });
      setUploadFile(null);
      setUploadMode('file');
      loadDocuments();
    } catch (err) {
      toast.error('Failed to add document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove from knowledge base?')) return;
    try {
      await knowledgeBaseAPI.delete(id);
      toast.success('Document removed');
      loadDocuments();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const filtered = documents.filter(d =>
    !search || d.document_title?.toLowerCase().includes(search.toLowerCase()) ||
    d.state?.toLowerCase().includes(search.toLowerCase()) ||
    d.regulation_citation?.toLowerCase().includes(search.toLowerCase())
  );

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', boxSizing: 'border-box' };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <div className="page-title">Knowledge Base</div>
          <div className="page-subtitle">Regulatory documents and references for AI compliance chat</div>
        </div>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          <Plus size={16} /> Add Document
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#1E40AF', fontFamily: 'var(--font-body)' }}>
        📚 Documents added here are referenced by the AI chat assistant when answering compliance questions. Add state regulations, EPA guidance, and facility-specific documents.
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          type="text"
          placeholder="Search documents..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 36, maxWidth: 380 }}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <BookOpen size={40} color="#CBD5E1" style={{ margin: '0 auto 14px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628', marginBottom: 6 }}>
            {search ? 'No documents found' : 'Knowledge base is empty'}
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>
            {search ? 'Try a different search term' : 'Add regulatory documents to improve AI responses'}
          </div>
          {!search && (
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              <Plus size={15} /> Add First Document
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(doc => (
            <div key={doc.id} className="card" style={{ padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BookOpen size={17} color="#0066FF" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: '#0A1628', marginBottom: 3 }}>
                  {doc.document_title}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: doc.content ? 6 : 0 }}>
                  {doc.document_type && <span>📋 {doc.document_type}</span>}
                  {doc.state && <span>📍 {doc.state}</span>}
                  {doc.regulation_citation && <span>§ {doc.regulation_citation}</span>}
                </div>
                {doc.content && (
                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {doc.content}
                  </div>
                )}
                {doc.tags && doc.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {doc.tags.map((tag, i) => (
                      <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#F1F5F9', color: '#475569', fontFamily: 'var(--font-body)' }}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => handleDelete(doc.id)} className="icon-btn" style={{ color: '#DC2626', borderColor: '#FECACA', flexShrink: 0 }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>Add to Knowledge Base</div>
              <button onClick={() => setShowModal(false)} className="icon-btn"><X size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Document Title *</label>
                <input type="text" required value={formData.documentTitle} onChange={e => setFormData({...formData, documentTitle: e.target.value})} placeholder="e.g. Ohio EPA Title V Requirements" style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Document Type</label>
                  <select value={formData.documentType} onChange={e => setFormData({...formData, documentType: e.target.value})} style={inputStyle}>
                    <option value="">Select type...</option>
                    <option value="regulation">Regulation</option>
                    <option value="guidance">EPA Guidance</option>
                    <option value="air_permit">Air Permit</option>
                    <option value="policy">State Policy</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">State</label>
                  <select value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} style={inputStyle}>
                    <option value="">Federal / All states</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Regulation Citation</label>
                <input type="text" value={formData.regulationCitation} onChange={e => setFormData({...formData, regulationCitation: e.target.value})} placeholder="e.g. 40 CFR Part 70" style={inputStyle} />
              </div>
              {/* Upload mode toggle */}
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Add Content Via</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button type="button" onClick={() => setUploadMode('file')}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${uploadMode === 'file' ? '#0066FF' : '#E2E8F0'}`, background: uploadMode === 'file' ? '#EFF6FF' : 'white', color: uploadMode === 'file' ? '#0066FF' : '#374151', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    📎 Upload File
                  </button>
                  <button type="button" onClick={() => setUploadMode('text')}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${uploadMode === 'text' ? '#0066FF' : '#E2E8F0'}`, background: uploadMode === 'text' ? '#EFF6FF' : 'white', color: uploadMode === 'text' ? '#0066FF' : '#374151', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    ✏️ Paste Text
                  </button>
                </div>

                {uploadMode === 'file' ? (
                  <div>
                    <div
                      onClick={() => document.getElementById('kb-file-input').click()}
                      style={{ border: '2px dashed #E2E8F0', borderRadius: 10, padding: '24px', textAlign: 'center', cursor: 'pointer', background: '#FAFAFA', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#0066FF'; e.currentTarget.style.background = '#EFF6FF'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = '#FAFAFA'; }}
                    >
                      <input id="kb-file-input" type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: 'none' }}
                        onChange={e => { setUploadFile(e.target.files[0]); if (e.target.files[0] && !formData.documentTitle) setFormData({...formData, documentTitle: e.target.files[0].name.replace(/\.[^.]+$/, '')}); }} />
                      <Upload size={28} color="#94a3b8" style={{ margin: '0 auto 10px', display: 'block' }} />
                      {uploadFile ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>📄 {uploadFile.name}</div>
                          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4, fontFamily: 'var(--font-body)' }}>{(uploadFile.size / 1024).toFixed(0)} KB</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)' }}>Drop file here or click to browse</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontFamily: 'var(--font-body)' }}>PDF, Word (.docx), or Text files</div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <textarea value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} rows={4} placeholder="Paste the key text or regulatory content here..." style={{ ...inputStyle, resize: 'vertical' }} />
                )}
              </div>
              <div style={{ marginBottom: 20 }}>
                <label className="form-label">Tags (comma separated)</label>
                <input type="text" value={formData.tags} onChange={e => setFormData({...formData, tags: e.target.value})} placeholder="e.g. Title V, NOx, monitoring" style={inputStyle} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-ghost" disabled={uploading}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={uploading} style={{ opacity: uploading ? 0.7 : 1 }}>
                  {uploading ? '⏳ Processing...' : 'Add to Knowledge Base'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeBase;
